import React, { useEffect, useState } from "react";

/**
 * A custom hook for:
 * - Watching the dependencies and forcing re-render current component
 * - Forcing re-render the component manually
 * 
 * @param deps - a list of dependencies to watch
 * 
 * @param callback - a callback on dependencies change (similar to useEffect())
 *
 * @returns an object with function forceRender()
 *
 * @example
 * ```ts
 * // Watches for myValue change, then forces the component to re-render:
  const updater = useWatchAndRender([props.myValue]);
 * ```
 * ```ts
 * // Watches for myValue change then, fires callback then forces the component to re-render:
  const updater = useWatchAndRender([props.myValue], () => {
    console.log("callback!!!")    
  });
 * ```
  * ```ts
 * // Manually forces component to re-render:
  updater.forceRender();
 * ```
 */
const useWatchAndRender = (deps: React.DependencyList, callback?: () => void): { forceRender: () => void} => {
  const [, setValue] = useState<object>();
  const update = () => {
    setValue({});
    typeof callback === "function" && callback();
  };

  useEffect(() => update(), deps);

  return {
    forceRender: update
  };
};

export default useWatchAndRender;
